import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatPercent } from "../../../../utils/formatNumber";
import { adTypesByChannel } from "./AdTypeSection";
import getMetricSpec, { getFormatter } from "../../../merchandising/utils/metrics";
import { useAppSelector } from "../../../../redux/hooks";
import { getTrendColor } from "../../../../components/custom/summaryReport/utils";
import { useResolvedLabels } from "../../../../i18n/useResolvedLabels";

const Card = ({ name, title, selected, setSelected, data, cardMetrics }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { t } = useTranslation(["advertising"]);

  return (
    <div
      className="me-4 p-4 mb-3"
      style={{
        height: "150px",
        cursor: "pointer",
        boxShadow: "none",
        border: `2px solid ${selected?.value === data?.ad_type ? "rgb(15,195,152, 0.50)" : "rgb(238, 238, 238)"}`,
        borderRadius: "25px",
        backgroundColor: selected?.value === data?.ad_type ? "rgb(15,195,152, 0.15)" : null,
      }}
      onClick={() =>
        selected?.value === data?.ad_type ?
          setSelected({ label: t(($) => $.advertising["All Media Plan Types"]), value: "all" })
        : setSelected({ label: name, value: data?.ad_type })
      }
    >
      <Row className="ps-4">
        <Col xs={12} className="pb-1">
          <span className="fs-4 fw-bold">{title}</span>
        </Col>
      </Row>
      <Row className="d-flex h-100 align-items-center">
        {cardMetrics.map((metric) => {
          const { title, valueAccessor, prevValueAccessor, overrideValue, overridePrevValue } = metric;

          const metricSpec = getMetricSpec(valueAccessor);
          const formatter = getFormatter(marketPlace, metricSpec.format, metricSpec);

          const value =
            overrideValue ? overrideValue
            : valueAccessor ? formatter(data[valueAccessor])
            : null;
          const prevValue =
            overridePrevValue ? overridePrevValue
            : prevValueAccessor ? data[prevValueAccessor]
            : null;

          return (
            <Col key={`${metricSpec.id}-${valueAccessor}`} className="fs-5 text-center align-items-flex-start">
              <span style={{ whiteSpace: "nowrap" }}>{t(title)}</span>
              <span className="fs-3 fw-bold d-block">{value}</span>
              {prevValue ?
                <span
                  style={{
                    color: getTrendColor(prevValue, prevValueAccessor, "rgb(15, 195, 152)", "rgb(240, 60, 103)"),
                  }}
                  className="fw-bold"
                >
                  {formatPercent(prevValue, {
                    decimalPlaces: 1,
                    addPlus: true,
                  })}
                </span>
              : <br />}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

const AdTypeCards = ({ selectedCard, setSelectedCard, data }) => {
  const channel = useAppSelector((state) => state.channel);
  const adTypes = useResolvedLabels(adTypesByChannel[channel.currentChannel ?? "amazon"] ?? [], ["advertising"]);

  const cardStats = [
    {
      title: ($) => $.advertising["# of Active Plans"],
      valueAccessor: "active_ad_count",
    },
    {
      title: ($) => $.advertising["Ad Sales"],
      valueAccessor: "ad_sales",
      prevValueAccessor: "ad_sales_percent",
    },
    {
      title: ($) => $.advertising["Ad Spend"],
      valueAccessor: "cost",
      prevValueAccessor: "cost_percent",
    },
    {
      title: ($) => $.advertising["ACOS"],
      valueAccessor: "acos",
      prevValueAccessor: "acos_delta",
    },
    {
      title: ($) => $.advertising["CTR"],
      valueAccessor: "ctr",
      prevValueAccessor: "ctr_delta",
    },
    {
      title: ($) => $.advertising["Clicks"],
      valueAccessor: "clicks",
      prevValueAccessor: "clicks_percent",
    },
    {
      title: ($) => $.advertising["Impressions"],
      valueAccessor: "impressions",
      prevValueAccessor: "impressions_percent",
    },
  ];

  return (
    <>
      {adTypes
        ?.map((type) => {
          const adTypeData = data?.find((row) => row.ad_type === type.value) ?? [];
          return { ...type, adTypeData };
        })
        ?.sort((a, b) => {
          if (!b.adTypeData?.ad_sales) {
            return -1;
          }
          return b.adTypeData?.ad_sales - a.adTypeData?.ad_sales;
        })
        ?.filter((type) => {
          return type?.adTypeData?.ad_sales || type?.adTypeData?.cost;
        })
        ?.map((type) => (
          <Card
            key={`${channel.currentChannel}-${type.value}`}
            title={type.label}
            name={type.label}
            selected={selectedCard}
            setSelected={setSelectedCard}
            data={type.adTypeData ?? []}
            cardMetrics={cardStats}
          />
        ))}
    </>
  );
};

export default AdTypeCards;
